import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { useWaarschuwBijVerlaten } from '@/hooks/useWaarschuwBijVerlaten';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface VerlatenWaarschuwingProps {
  /** Waar zolang er werk is dat bij het verlaten van dit scherm verloren gaat. */
  actief: boolean;
  titel: string;
  beschrijving: string;
  /** De tekst op de knop die het verlaten alsnog doorzet. */
  verlatenLabel?: string;
}

/**
 * Vragen of iemand dit scherm echt wil verlaten, langs beide uitgangen.
 *
 * Een consult staat alleen in het geheugen van dit component. Er zijn twee manieren
 * om het kwijt te raken en ze vragen elk om iets anders:
 *
 *   - De tab, het venster of een herlaadactie. Daar gaat de browser over; het enige
 *     dat de applicatie kan doen is `beforeunload` afwijzen, waarna de browser zijn
 *     eigen dialoogvenster toont met zijn eigen tekst. Zie useWaarschuwBijVerlaten.
 *   - Navigeren binnen de applicatie: het tandwiel naar de instellingen, de
 *     handleiding, of de terugknop van de browser. Die verlaten de pagina niet, dus
 *     `beforeunload` komt er niet aan te pas. react-router houdt ze tegen met
 *     useBlocker -- en omdat de applicatie hier zelf over gaat, kan het dialoogvenster
 *     wél zeggen wat er op het spel staat.
 *
 * De tweede is de reden dat App.tsx een data router gebruikt: useBlocker bestaat
 * niet zonder.
 */
export function VerlatenWaarschuwing({
  actief,
  titel,
  beschrijving,
  verlatenLabel = 'Toch verlaten',
}: VerlatenWaarschuwingProps) {
  useWaarschuwBijVerlaten(actief);

  const blocker = useBlocker(actief);

  /**
   * Het werk kan klaar zijn terwijl de vraag nog openstaat -- de SOEP is verzonden,
   * of het transcript is gewist. Blijft de blocker dan hangen, dan houdt hij de
   * navigatie tegen die er niet meer toe doet, en is er geen dialoogvenster meer om
   * hem mee vrij te geven.
   */
  useEffect(() => {
    if (!actief && blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [actief, blocker]);

  return (
    <AlertDialog
      open={blocker.state === 'blocked'}
      onOpenChange={(open) => {
        // Wegklikken met Escape of een klik naast het venster telt als blijven.
        if (!open && blocker.state === 'blocked') blocker.reset();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{titel}</AlertDialogTitle>
          <AlertDialogDescription>{beschrijving}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => blocker.reset?.()}>Blijven</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => blocker.proceed?.()}
          >
            {verlatenLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
